import { Box } from '@customink/pigment-react';
import {
  getTotalReorderDiscount,
  useMobileBreakpoint,
  usePricing,
} from '@customink/project-service-react-components';
import type { ProjectItem as ProjectItemType } from '@customink/project-service-react-components';
import Big from 'big.js';

import { LineItem } from '../LineItem/LineItem';

import { useGroupDeliveryFee } from '@/hooks/Project';
import { colorSmoke, spacing16 } from '@/utils/designTokens';
type CartSubtotalsProps = {
  designCid?: string;
  projectItems: ProjectItemType[];
};

export function CartSubtotals({ designCid, projectItems }: CartSubtotalsProps) {
  const {
    data: pricingData,
    isLoading: loading,
    isFetching: fetching,
  } = usePricing();

  const groupQuantity = Big(
    projectItems.reduce(
      (accumulator, item) => accumulator + (item.totalQuantity || 0),
      0,
    ),
  );
  const groupQuantityDisplay = groupQuantity.gt(0)
    ? groupQuantity.toFixed()
    : '--';

  const groupPricingData = designCid ? pricingData.grouped[designCid] : null;

  const groupSubTotal = Big(groupPricingData?.total_base_price || '0');
  const groupSubTotalDisplay = groupSubTotal.gt(0)
    ? groupSubTotal.toFixed(2)
    : '--';

  const groupDeliveryFee = useGroupDeliveryFee(
    designCid as string,
    projectItems,
  );

  const groupReorderDiscount = groupPricingData
    ? getTotalReorderDiscount([groupPricingData])
    : Big(0);
  const groupReorderDiscountDisplay = groupReorderDiscount.abs().toFixed(2);

  const groupReorderSubtotal = Big(groupPricingData?.total_price || '0');
  const groupReorderSubtotalDisplay = groupReorderSubtotal.toFixed(2);

  const isMobile = useMobileBreakpoint();

  const isPricingLoading = loading && !fetching;

  const lineItemSxName = {
    fontSize: '14px',
    fontWeight: 'bold',
  };

  const lineItemSxValue = {
    fontSize: '14px',
  };

  return (
    <Box
      data-testid="cart-subtotals"
      marginBottom={spacing16}
      sx={{
        padding: !isMobile ? '0 0 0 1rem' : '1rem 0 0',
        borderLeft: !isMobile ? `1px solid ${colorSmoke}` : 'none',
        marginBottom: '8px',
        display: 'flex',
        gap: '0.5rem',
        flexDirection: 'column',
        flex: 1,
        maxWidth: !isMobile ? '220px' : 'unset',
      }}
    >
      <LineItem
        isLoading={isPricingLoading}
        name="Qty"
        sxName={lineItemSxName}
        sxValue={lineItemSxValue}
        value={groupQuantityDisplay}
      />
      <LineItem
        isLoading={isPricingLoading}
        name="Subtotal"
        sxName={lineItemSxName}
        sxValue={lineItemSxValue}
        value={`$${groupSubTotalDisplay}`}
      />
      {groupReorderDiscount.lt(0) && (
        <>
          <LineItem
            isLoading={isPricingLoading}
            name="Reorder Discount"
            sxName={lineItemSxName}
            sxValue={lineItemSxValue}
            value={`-$${groupReorderDiscountDisplay}`}
          />
          <LineItem
            isLoading={isPricingLoading}
            name="Reorder Subtotal"
            sxName={lineItemSxName}
            sxValue={lineItemSxValue}
            value={`$${groupReorderSubtotalDisplay}`}
          />
        </>
      )}
      <LineItem
        isLoading={isPricingLoading}
        name="Delivery"
        sxName={lineItemSxName}
        sxValue={lineItemSxValue}
        value={`$${groupDeliveryFee}`}
      />
    </Box>
  );
}
